/* ==========================================================================
   SIDEKICK 2.0 LAUNCH PAGE — JavaScript
   ========================================================================== */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     HELPER: Autoplay with Low Power Mode fallback
     Tries play() immediately. If blocked (iOS Low Power Mode), waits for
     the first user touch/scroll to retry — iOS counts these as user
     interactions that satisfy the autoplay requirement.
     ----------------------------------------------------------------------- */
  var _touchPlayRegistered = false;
  var _pendingVideos = [];
  function autoplayVideo(video) {
    if (!video || video.tagName !== 'VIDEO') return;
    video.muted = true;
    var p = video.play();
    if (p && p.catch) {
      p.catch(function () {
        _pendingVideos.push(video);
        if (!_touchPlayRegistered) {
          _touchPlayRegistered = true;
          function playAll() {
            _pendingVideos.forEach(function (v) { v.play().catch(function () {}); });
            _pendingVideos = [];
            document.removeEventListener('touchstart', playAll);
            document.removeEventListener('scroll', playAll);
            document.removeEventListener('click', playAll);
            document.removeEventListener('keydown', playAll);
          }
          document.addEventListener('touchstart', playAll, { once: true, passive: true });
          document.addEventListener('scroll', playAll, { once: true, passive: true });
          document.addEventListener('click', playAll, { once: true, passive: true });
          document.addEventListener('keydown', playAll, { once: true, passive: true });
        }
      });
    }
  }

  /* -----------------------------------------------------------------------
     HELPER: Tab Switcher
     Generic click-to-toggle for cards/tabs + panels.
     ----------------------------------------------------------------------- */
  function initTabSwitcher(triggers, panels, activateFirst) {
    if (!triggers.length) return;
    triggers.forEach(function (trigger, i) {
      trigger.addEventListener('click', function () {
        triggers.forEach(function (t) { t.classList.remove('is-active'); });
        panels.forEach(function (p) { p.classList.remove('is-active'); });
        trigger.classList.add('is-active');
        if (panels[i]) panels[i].classList.add('is-active');
      });
    });
    if (activateFirst) {
      triggers[0].classList.add('is-active');
      if (panels[0]) panels[0].classList.add('is-active');
    }
  }

  /* -----------------------------------------------------------------------
     SCROLL REVEAL
     ----------------------------------------------------------------------- */
  var revealObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15 }
  );

  document.querySelectorAll('[data-sk2-reveal]').forEach(function (el) {
    revealObserver.observe(el);
  });

  /* -----------------------------------------------------------------------
     SMOOTH SCROLL LINKS — intercepts anchor links to smooth scroll
     without adding a hash to the URL
     ----------------------------------------------------------------------- */
  var scrollNextBtn = document.querySelector('[data-sk2-scroll-next]');
  if (scrollNextBtn) {
    scrollNextBtn.addEventListener('click', function () {
      var wordmark = document.querySelector('.sk2-wordmark');
      if (wordmark) {
        var top = wordmark.getBoundingClientRect().bottom + window.pageYOffset - window.innerHeight - 50;
        window.scrollTo({ top: top, behavior: 'smooth' });
      } else {
        var section = this.closest('.shopify-section') || this.closest('.sk2');
        if (section) {
          var top = section.getBoundingClientRect().bottom + window.pageYOffset;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      }
    });
  }

  /* -----------------------------------------------------------------------
     SMOOTH SCROLL
     ----------------------------------------------------------------------- */
  document.querySelectorAll('.sk2 a[href^="#"], .sk2-nav a[href^="#"]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      var id = this.getAttribute('href').substring(1);
      var target = document.getElementById(id);
      if (target) {
        e.preventDefault();
        var navOffset = 50;
        // Wordmark: scroll to end of animation (same as Explore button)
        if (id === 'wordmark') {
          var top = target.getBoundingClientRect().bottom + window.pageYOffset - window.innerHeight - 50;
          window.scrollTo({ top: top, behavior: 'smooth' });
        } else {
          var top = target.getBoundingClientRect().top + window.pageYOffset - navOffset;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      }
    });
  });

  /* -----------------------------------------------------------------------
     HERO — Timed slideshow
     ----------------------------------------------------------------------- */
  var heroSection = document.querySelector('[data-sk2-hero]');
  if (heroSection) {
    var hSlides = heroSection.querySelectorAll('[data-sk2-hero-slide]');
    var hTabs = heroSection.querySelectorAll('[data-sk2-hero-tab]');
    var hPauseBtn = heroSection.querySelector('.sk2-hero__pause');
    var hPauseIcon = heroSection.querySelector('.sk2-hero__pause-icon');
    var hPlayIcon = heroSection.querySelector('.sk2-hero__play-icon');
    var hCurrent = 0;
    var hTotal = hSlides.length;
    var hTimer = null;
    var hPaused = false;
    var hVisible = false;
    var H_DURATION = 4000;

    heroSection.style.setProperty('--sk2-hero-duration', H_DURATION + 'ms');

    /* Ensure slide 0 is active regardless of Shopify block order.
       Add --ready class to reveal after setup (prevents flash). */
    hSlides.forEach(function (s) { s.classList.remove('is-active'); });
    hTabs.forEach(function (t) { t.classList.remove('is-active'); });
    if (hSlides[0]) {
      hSlides[0].classList.add('is-active');
      hSlides[0].classList.add('is-intro');
    }
    if (hTabs[0]) hTabs[0].classList.add('is-active');
    heroSection.classList.add('sk2-hero--ready');

    var hSlideNav = heroSection.querySelector('.sk2-hero__slide-nav');

    /* Lazy-load a slide's image from data-sk2-hero-lazy */
    function hLoadSlide(slide) {
      if (!slide || slide.querySelector('img') || !slide.hasAttribute('data-sk2-hero-lazy')) return;
      var src = slide.getAttribute('data-sk2-hero-lazy');
      var img = new Image();
      img.className = 'sk2-hero__slide-img';
      img.decoding = 'async';
      img.src = src;
      slide.appendChild(img);
      slide.removeAttribute('data-sk2-hero-lazy');
    }

    /* Preload the next slide so it's ready before the timer fires */
    function hPreloadNext(fromIdx) {
      var nextIdx = (fromIdx + 1) % hTotal;
      hLoadSlide(hSlides[nextIdx]);
    }

    function hGoTo(idx) {
      if (idx >= hTotal) idx = 0;
      if (idx < 0) idx = hTotal - 1;

      var prev = hCurrent;
      hCurrent = idx;

      /* Load the target slide image if not yet loaded */
      hLoadSlide(hSlides[hCurrent]);

      /* Outgoing slide: fade out. Remove is-intro first and force
         reflow so the browser has a computed state to transition from. */
      if (hSlides[prev]) {
        hSlides[prev].classList.remove('is-intro');
        hSlides[prev].classList.remove('is-active');
        void hSlides[prev].offsetWidth; /* force reflow */
        hSlides[prev].classList.add('is-leaving');
        (function (slide) {
          setTimeout(function () { slide.classList.remove('is-leaving'); }, 900);
        })(hSlides[prev]);
      }
      if (hTabs[prev]) {
        hTabs[prev].classList.remove('is-active');
        hTabs[prev].classList.remove('is-timing');
        hTabs[prev].classList.remove('is-paused');
      }

      /* Incoming slide */
      if (hSlides[hCurrent]) hSlides[hCurrent].classList.add('is-active');

      /* Preload the next slide in the background */
      hPreloadNext(hCurrent);
      if (hTabs[hCurrent]) {
        hTabs[hCurrent].classList.add('is-active');
        if (hPaused) {
          hTabs[hCurrent].classList.add('is-paused');
        } else if (hVisible) {
          void hTabs[hCurrent].offsetWidth;
          hTabs[hCurrent].classList.add('is-timing');
        }
        /* Auto-scroll active tab into view on mobile */
        var pill = hTabs[hCurrent].parentElement;
        if (pill && pill.scrollWidth > pill.clientWidth) {
          var tabLeft = hTabs[hCurrent].offsetLeft;
          var tabWidth = hTabs[hCurrent].offsetWidth;
          var pillWidth = pill.clientWidth;
          pill.scrollTo({ left: tabLeft - (pillWidth / 2) + (tabWidth / 2), behavior: 'smooth' });
        }
      }
    }

    function hSchedule() {
      hStopTimer();
      if (!hVisible || hPaused || hTotal < 2) return;
      /* Preload the next slide while the current one is displayed */
      hPreloadNext(hCurrent);
      if (hTabs[hCurrent]) hTabs[hCurrent].classList.add('is-timing');
      hTimer = setTimeout(function () {
        hGoTo(hCurrent + 1);
        hSchedule();
      }, H_DURATION);
    }

    function hStopTimer() {
      clearTimeout(hTimer);
      hTimer = null;
    }

    /* Tab clicks — pause the slideshow, skip if already on this slide */
    hTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var idx = parseInt(tab.getAttribute('data-sk2-hero-tab'), 10);
        hPaused = true;
        hStopTimer();
        if (hPauseIcon) hPauseIcon.style.display = 'none';
        if (hPlayIcon) hPlayIcon.style.display = 'block';
        if (hTabs[hCurrent]) hTabs[hCurrent].classList.remove('is-timing');
        if (idx === hCurrent) {
          if (hTabs[hCurrent]) hTabs[hCurrent].classList.add('is-paused');
          return;
        }
        hGoTo(idx);
      });
    });

    /* Pause / play */
    if (hPauseBtn) {
      hPauseBtn.addEventListener('click', function () {
        hPaused = !hPaused;
        if (hPaused) {
          hStopTimer();
          if (hTabs[hCurrent]) {
            hTabs[hCurrent].classList.remove('is-timing');
            hTabs[hCurrent].classList.add('is-paused');
          }
          if (hPauseIcon) hPauseIcon.style.display = 'none';
          if (hPlayIcon) hPlayIcon.style.display = 'block';
        } else {
          hTabs.forEach(function (t) { t.classList.remove('is-paused'); });
          if (hPauseIcon) hPauseIcon.style.display = 'block';
          if (hPlayIcon) hPlayIcon.style.display = 'none';
          hSchedule();
        }
      });
    }

    /* Visibility gating */
    new IntersectionObserver(function (entries) {
      hVisible = entries[0].isIntersecting;
      if (hVisible && !hPaused) {
        hSchedule();
      } else {
        hStopTimer();
        if (hTabs[hCurrent]) hTabs[hCurrent].classList.remove('is-timing');
      }
    }, { threshold: 0.1 }).observe(heroSection);

    /* Tab hidden */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        hStopTimer();
        if (hTabs[hCurrent]) hTabs[hCurrent].classList.remove('is-timing');
      } else if (hVisible && !hPaused) {
        hSchedule();
      }
    });
  }

  /* -----------------------------------------------------------------------
     STICKY NAV — single observer for all tracked sections
     ----------------------------------------------------------------------- */
  var nav = document.querySelector('.sk2-nav');
  var hero = document.querySelector('.sk2-hero');

  if (nav && hero) {
    var navLinks = nav.querySelectorAll('.sk2-nav__link[href^="#"]');
    var trackedSections = [];
    var activeLink = null;

    var navTopBtn = nav.querySelector('[data-sk2-nav-top]');
    if (navTopBtn) {
      navTopBtn.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    navLinks.forEach(function (link) {
      var id = link.getAttribute('href').substring(1);
      var section = document.getElementById(id);
      if (section) trackedSections.push({ el: section, link: link });
    });

    /* Show/hide nav based on hero visibility */
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          nav.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    ).observe(hero);

    var navPill = nav.querySelector('.sk2-nav__pill');

    /* Active section tracking — scroll-position based */
    function updateActiveNav() {
      var scrollY = window.pageYOffset + 150;
      var current = null;
      for (var i = trackedSections.length - 1; i >= 0; i--) {
        var s = trackedSections[i];
        if (s.el.offsetTop <= scrollY) {
          current = s;
          break;
        }
      }
      if (current && current.link !== activeLink) {
        if (activeLink) activeLink.classList.remove('is-active');
        current.link.classList.add('is-active');
        activeLink = current.link;
        if (navPill && navPill.scrollWidth > navPill.clientWidth) {
          var pillRect = navPill.getBoundingClientRect();
          var linkRect = current.link.getBoundingClientRect();
          var target = navPill.scrollLeft + (linkRect.left - pillRect.left)
            - (pillRect.width / 2) + (linkRect.width / 2);
          navPill.scrollTo({ left: target, behavior: 'smooth' });
        }
      }
    }

    var navScrollTicking = false;
    window.addEventListener('scroll', function () {
      if (!navScrollTicking) {
        requestAnimationFrame(function () {
          updateActiveNav();
          navScrollTicking = false;
        });
        navScrollTicking = true;
      }
    }, { passive: true });
    updateActiveNav();
  }

  /* -----------------------------------------------------------------------
     WORDMARK — Background video + scroll word reveal + headings + modal
     ----------------------------------------------------------------------- */
  var wordmark = document.querySelector('[data-sk2-wordmark]');
  if (wordmark) {
    var wmHeadings = wordmark.querySelectorAll('[data-sk2-wm-heading]');
    var wmSentences = wordmark.querySelectorAll('[data-sk2-wm-sentence]');
    var wmCta = wordmark.querySelector('[data-sk2-wm-launch]');
    var wmFinalWrap = wordmark.querySelector('[data-sk2-wm-final-wrap]');
    var wmModal = wordmark.querySelector('[data-sk2-wm-modal]');
    var wmBgVideo = wordmark.querySelector('video.sk2-wordmark__video');
    var wmTotal = wmHeadings.length;
    var wmTicking = false;
    var wmNearViewport = false;

    /* Index words per sentence so we can light them progressively */
    var wmSentenceWords = [];
    wmSentences.forEach(function (sent) {
      wmSentenceWords.push(sent.querySelectorAll('.sk2-wordmark__pword'));
    });

    function updateWordmark() {
      wmTicking = false;
      if (!wmNearViewport) return;

      var rect = wordmark.getBoundingClientRect();
      var ih = window.innerHeight;
      /* The section sits behind the hero (margin-top: -100vh), so its rect.top
         starts at 0 even on page load. Run the animation over the portion of
         scroll AFTER the hero is mostly out of view, leaving a short rest at
         the end before the sticky stage scrolls away. */
      var animStart = -ih * 0.6;          // rect.top at progress 0 — kicks in while hero is still scrolling out
      var animEnd = -(wordmark.offsetHeight - ih * 1.1); // minimal rest before unstick
      var progress = Math.max(0, Math.min(1, (rect.top - animStart) / (animEnd - animStart)));

      /* Scroll budget:
         - Heading 0 visible from the start (sentence 0 lights through phase 0).
         - Each subsequent heading enters at its phaseStart, sliding the stack up.
         - Closing line appears once all sentences are complete, pushing headings up. */
      var endGap = 0.05;
      var headingsBudget = 1 - endGap;
      var perHeading = headingsBudget / wmTotal;

      /* Heading 0 is always visible; later headings enter when the previous
         heading's sentence is ~85% lit (not at the start of the phase) */
      var visibleCount = 1;
      for (var i = 1; i < wmTotal; i++) {
        if (progress >= i * perHeading + perHeading * 0.6) visibleCount = i + 1;
      }

      /* Position each heading + light its sentence words */
      for (var i = 0; i < wmTotal; i++) {
        var phaseStart = i * perHeading;
        var phaseEnd = phaseStart + perHeading;
        var localProgress = (progress - phaseStart) / (phaseEnd - phaseStart);
        localProgress = Math.max(0, Math.min(1, localProgress));

        var h = wmHeadings[i];
        if (i < visibleCount) {
          var stackOffset = (visibleCount - 1 - i) * -110;
          h.style.opacity = '1';
          h.style.transform = 'translateY(' + stackOffset + '%)';
        } else {
          h.style.opacity = '0';
          h.style.transform = 'translateY(40px)';
        }
        h.style.setProperty('--strike', i < visibleCount ? localProgress : 0);

        var words = wmSentenceWords[i];
        if (words && words.length) {
          var litCount = Math.floor(localProgress * words.length + 0.0001);
          for (var w = 0; w < words.length; w++) {
            words[w].classList.toggle('is-lit', w < litCount);
          }
        }
      }

      var finalThreshold = 1 - endGap;
      if (wmFinalWrap) wmFinalWrap.classList.toggle('is-visible', progress >= finalThreshold);
    }

    /* Gate scroll work — only run when section is near viewport */
    new IntersectionObserver(
      function (entries) {
        wmNearViewport = entries[0].isIntersecting;
      },
      { rootMargin: '200px 0px' }
    ).observe(wordmark);

    if (wmBgVideo) autoplayVideo(wmBgVideo);

    window.addEventListener('scroll', function () {
      if (!wmTicking) {
        requestAnimationFrame(updateWordmark);
        wmTicking = true;
      }
    });

    /* Sync visibility from current scroll position so the first updateWordmark()
       call computes correctly on page-reload-while-scrolled. */
    var wmInitRect = wordmark.getBoundingClientRect();
    wmNearViewport = wmInitRect.bottom > -200 && wmInitRect.top < window.innerHeight + 200;
    updateWordmark();

    /* Recompute on resize so animation state stays in sync with new viewport dims. */
    var wmResizeRaf = false;
    window.addEventListener('resize', function () {
      if (wmResizeRaf) return;
      wmResizeRaf = true;
      requestAnimationFrame(function () {
        wmResizeRaf = false;
        var r = wordmark.getBoundingClientRect();
        wmNearViewport = r.bottom > -200 && r.top < window.innerHeight + 200;
        updateWordmark();
      });
    });

    /* --- Video modal --- */
    if (wmModal && wmCta) {
      var wmVideoHost = wmModal.querySelector('[data-sk2-wm-video]');
      var wmYtId = wmVideoHost && wmVideoHost.getAttribute('data-yt');

      function wmOpenModal() {
        if (!wmYtId) return;
        if (wmBgVideo) { try { wmBgVideo.pause(); } catch (e) {} }
        wmVideoHost.innerHTML = '<iframe src="https://www.youtube.com/embed/' + wmYtId + '?autoplay=1&rel=0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>';
        wmModal.hidden = false;
        requestAnimationFrame(function () { wmModal.classList.add('is-open'); });
      }

      function wmCloseModal() {
        wmModal.classList.remove('is-open');
        setTimeout(function () {
          wmModal.hidden = true;
          /* Tear down the iframe FIRST so it releases media focus, then
             resume the background video. */
          if (wmVideoHost) wmVideoHost.innerHTML = '';
          if (wmBgVideo) { wmBgVideo.play().catch(function () {}); }
        }, 300);
      }

      wmCta.addEventListener('click', wmOpenModal);
      /* Any click that bubbles up to the modal element is by definition NOT
         inside the YouTube iframe (cross-origin clicks don't escape iframes),
         so a single handler closes the modal whenever the user clicks
         anywhere off the player — backdrop, frame border, or close button. */
      wmModal.addEventListener('click', wmCloseModal);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && wmModal.classList.contains('is-open')) wmCloseModal();
      });
    }
  }

  /* -----------------------------------------------------------------------
     KICKBACK EXPLAINER — Toggles .is-engaged once the user scrolls into it
     ----------------------------------------------------------------------- */
  var kickback = document.querySelector('[data-sk2-kickback]');
  if (kickback) {
    // Set --kb-vh as a fixed pixel value so card transforms don't recalculate
    // when mobile Safari's URL bar shows/hides (which changes vh units)
    kickback.style.setProperty('--kb-vh', window.innerHeight + 'px');
    var kbTicking = false;
    var kbVisible = false;
    var kbPhase1 = kickback.querySelector('.sk2-kickback__phase--reveal');
    var kbBgBlur = kickback.querySelector('.sk2-kickback__bg-blur');
    var kbBgOverlay = kickback.querySelector('.sk2-kickback__bg-overlay');
    var kbBgLines = kickback.querySelector('.sk2-kickback__bg-lines');
    var kbRevealFrozen = false;
    var kbPhase2 = kickback.querySelector('[data-sk2-kb-phase2]');
    var kbCardsCol = kickback.querySelector('.sk2-kickback__cards-col');
    var kbMech = kickback.querySelector('.sk2-kickback__mech');
    var kbCardEls = kbPhase2 ? Array.prototype.slice.call(kbPhase2.querySelectorAll('.sk2-kickback__card')) : [];
    var kbCardCount = kbCardEls.length || 6;
    var kbLastActiveIdx = -1;
    var kbVideo = kickback.querySelector('[data-sk2-kb-video-primary]');
    var kbCompareEl = kickback.querySelector('[data-sk2-kb-video-comparison]');
    var kbCompareVideo = kbCompareEl && kbCompareEl.tagName === 'VIDEO' ? kbCompareEl : null;

    var kbMechVideo = kickback.querySelector('[data-sk2-kb-mech-video]');
    var kbExpandBtn = kickback.querySelector('[data-sk2-kb-expand]');
    var kbCollapseBtn = kickback.querySelector('[data-sk2-kb-collapse]');
    var kbComparisonOpen = false;
    var kbMobile = window.matchMedia('(max-width: 767px)').matches;

    var kbVideoPlaying = false;
    var kbMechVideoPlaying = false;

    function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

    function gateVideo(video, shouldPlay, stateRef) {
      if (!video) return stateRef.playing;
      if (shouldPlay && !stateRef.playing) {
        video.muted = true;
        var promise = video.play();
        if (promise && promise.then) {
          promise.then(function () { stateRef.playing = true; })
                 .catch(function () { stateRef.playing = false; });
        } else {
          stateRef.playing = true;
        }
      } else if (!shouldPlay && stateRef.playing) {
        stateRef.playing = false;
        video.pause();
      }
      return stateRef.playing;
    }
    var kbVideoState = { playing: false };
    var kbMechVideoState = { playing: false };

    // Settle cards 2+ behind the front card so they tween via CSS transition
    // instead of being recalculated on every scroll frame
    function settleCards(cardsVal) {
      var activeIdx = Math.min(Math.floor(cardsVal), kbCardCount - 1);
      if (activeIdx === kbLastActiveIdx) return;
      for (var c = 0; c < kbCardCount; c++) {
        if (!kbCardEls[c]) continue;
        var behindCount = activeIdx - c;
        if (behindCount >= 2) {
          var depthC = Math.min(behindCount, 3);
          kbCardEls[c].style.setProperty('--enter', '1');
          kbCardEls[c].style.setProperty('--depth-c', String(depthC));
          if (!kbCardEls[c].classList.contains('is-settled')) {
            kbCardEls[c].classList.add('is-settled');
          }
        } else if (kbCardEls[c].classList.contains('is-settled')) {
          kbCardEls[c].classList.remove('is-settled');
          kbCardEls[c].style.removeProperty('--enter');
          kbCardEls[c].style.removeProperty('--depth-c');
        }
      }
      kbLastActiveIdx = activeIdx;
    }

    function updateKickback() {
      kbTicking = false;
      if (!kbVisible) return;
      var ih = window.innerHeight;

      if (kbMobile) {
        // On mobile all five phase-1 variables share the same progress value.
        // They must remain separate properties because the desktop branch (and
        // the CSS) references each one independently for different animations.
        var rectS = kickback.getBoundingClientRect();
        var pM = clamp01((ih - rectS.top) / (ih * 1.6));
        kickback.style.setProperty('--p', pM);
        kickback.style.setProperty('--ph', pM);
        kickback.style.setProperty('--pp', pM);
        kickback.style.setProperty('--pv', pM);
        kickback.style.setProperty('--pe', pM);

        // Drive bg effects via JS opacity (GPU path)
        if (kbRevealFrozen && pM < 1) {
          kbRevealFrozen = false;
          if (kbBgBlur) kbBgBlur.style.willChange = 'opacity';
          if (kbBgOverlay) kbBgOverlay.style.willChange = 'opacity';
          if (kbBgLines) kbBgLines.style.willChange = 'opacity';
        }
        if (!kbRevealFrozen) {
          if (kbBgBlur) kbBgBlur.style.opacity = pM.toFixed(2);
          if (kbBgOverlay) kbBgOverlay.style.opacity = (pM * 0.45).toFixed(2);
          if (kbBgLines) kbBgLines.style.opacity = (pM * 0.95).toFixed(2);
          if (pM >= 1) {
            kbRevealFrozen = true;
            if (kbBgBlur) kbBgBlur.style.willChange = 'auto';
            if (kbBgOverlay) kbBgOverlay.style.willChange = 'auto';
            if (kbBgLines) kbBgLines.style.willChange = 'auto';
          }
        }

        if (kbPhase2 && kbCardsCol && kbMech) {
          var rectMech = kbMech.getBoundingClientRect();
          var spacerPx = ih * 0.55 * kbCardCount;
          var pCol = clamp01((ih * 0.5 - rectMech.bottom) / spacerPx);
          var mobileCards = pCol * kbCardCount;
          kbPhase2.style.setProperty('--cards', mobileCards);
          settleCards(mobileCards);
        }
        return;
      }

      // Phase 1 — reveal (uses the reveal wrapper's own scroll range)
      var rect1 = kbPhase1 ? kbPhase1.getBoundingClientRect() : kickback.getBoundingClientRect();
      var range1 = (kbPhase1 ? kbPhase1.offsetHeight : kickback.offsetHeight) - ih;
      var animEnd = range1 * 0.7;
      var p = clamp01(-rect1.top / animEnd);
      var ph = clamp01(p / 0.5);
      var pp = clamp01((p - 0.5) / 0.35);
      var pv = clamp01((p - 0.1) / 0.4);
      var pe = clamp01((p - 0.85) / 0.15);
      kickback.style.setProperty('--p', p);
      kickback.style.setProperty('--ph', ph);
      kickback.style.setProperty('--pp', pp);
      kickback.style.setProperty('--pv', pv);
      kickback.style.setProperty('--pe', pe);

      // Drive bg effects via JS opacity (GPU path)
      if (kbRevealFrozen && p < 1) {
        kbRevealFrozen = false;
        if (kbBgBlur) kbBgBlur.style.willChange = 'opacity';
        if (kbBgOverlay) kbBgOverlay.style.willChange = 'opacity';
        if (kbBgLines) kbBgLines.style.willChange = 'opacity';
      }
      if (!kbRevealFrozen) {
        if (kbBgBlur) kbBgBlur.style.opacity = p.toFixed(2);
        if (kbBgOverlay) kbBgOverlay.style.opacity = (p * 0.45).toFixed(2);
        if (kbBgLines) kbBgLines.style.opacity = (p * 0.95).toFixed(2);
        if (p >= 1) {
          kbRevealFrozen = true;
          if (kbBgBlur) kbBgBlur.style.willChange = 'auto';
          if (kbBgOverlay) kbBgOverlay.style.willChange = 'auto';
          if (kbBgLines) kbBgLines.style.willChange = 'auto';
        }
      }

      gateVideo(kbVideo, pv >= 0.98 && !kbComparisonOpen, kbVideoState);

      // Phase 2 — mechanism + cards
      if (kbPhase2) {
        var rect2 = kbPhase2.getBoundingClientRect();
        var range2 = kbPhase2.offsetHeight - ih;
        var p2 = clamp01(-rect2.top / range2);
        var mechIn = clamp01(p2 / 0.10);
        var headerIn = clamp01((p2 - 0.02) / 0.10);
        var cards = clamp01((p2 - 0.14) / 0.78) * kbCardCount;
        kbPhase2.style.setProperty('--p2', p2);
        kbPhase2.style.setProperty('--mech-in', mechIn);
        kbPhase2.style.setProperty('--header-in', headerIn);
        kbPhase2.style.setProperty('--cards', cards);
        settleCards(cards);

        gateVideo(kbMechVideo, mechIn >= 0.98, kbMechVideoState);
      }
    }

    new IntersectionObserver(
      function (entries) {
        kbVisible = entries[0].isIntersecting;
        if (kbVisible) updateKickback();
      },
      { rootMargin: '200px 0px' }
    ).observe(kickback);

    window.addEventListener('scroll', function () {
      if (!kbTicking) {
        requestAnimationFrame(updateKickback);
        kbTicking = true;
      }
    });

    /* ----- Side-by-side comparison expand/collapse with glitch transition ----- */
    function openComparison() {
      if (kbComparisonOpen) return;
      kbComparisonOpen = true;

      if (kbVideo) {
        kbVideo.pause();
        kbVideoState.playing = false;
      }
      kickback.classList.add('is-comparison-open');
      if (kbCompareVideo) {
        var p = kbCompareVideo.play();
        if (p && p.catch) p.catch(function () {});
      }
    }

    function closeComparison() {
      if (!kbComparisonOpen) return;
      kickback.classList.remove('is-comparison-open');
      kbComparisonOpen = false;

      setTimeout(function () {
        if (kbCompareVideo) {
          kbCompareVideo.pause();
          try { kbCompareVideo.currentTime = 0; } catch (e) {}
        }
        updateKickback();
      }, 600);
    }

    if (kbExpandBtn) kbExpandBtn.addEventListener('click', openComparison);
    if (kbCollapseBtn) kbCollapseBtn.addEventListener('click', closeComparison);

    /* Sync visibility from current scroll position so the first updateKickback()
       call computes correctly on page-reload-while-scrolled. */
    var kbInitRect = kickback.getBoundingClientRect();
    kbVisible = kbInitRect.bottom > -200 && kbInitRect.top < window.innerHeight + 200;
    updateKickback();

    /* Recompute on resize so animation state stays in sync with new viewport
       dimensions and the mobile breakpoint flips correctly. */
    var kbResizeRaf = false;
    window.addEventListener('resize', function () {
      if (kbResizeRaf) return;
      kbResizeRaf = true;
      requestAnimationFrame(function () {
        kbResizeRaf = false;
        kbMobile = window.matchMedia('(max-width: 767px)').matches;
        var r = kickback.getBoundingClientRect();
        kbVisible = r.bottom > -200 && r.top < window.innerHeight + 200;
        updateKickback();
      });
    });

    if (kbMobile) {
      // Mech aside and header are always fully revealed on mobile
      if (kbPhase2) {
        kbPhase2.style.setProperty('--mech-in', 1);
        kbPhase2.style.setProperty('--header-in', 1);
      }
      // Gate videos on plain visibility since scroll progress doesn't drive them
      function observeVideo(el, state) {
        if (!el) return;
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { gateVideo(el, e.isIntersecting, state); });
        }, { threshold: 0.4 }).observe(el);
      }
      observeVideo(kbMechVideo, kbMechVideoState);
      observeVideo(kbVideo, kbVideoState);
    }
  }

  /* -----------------------------------------------------------------------
     VOICES — Timed group rotation
     ----------------------------------------------------------------------- */
  var voicesSection = document.querySelector('[data-sk2-voices]');
  if (voicesSection) {
    var vGroups = voicesSection.querySelectorAll('[data-sk2-vgroup]');
    var vTabs = voicesSection.querySelectorAll('[data-sk2-vtab]');
    var vPauseBtn = voicesSection.querySelector('.sk2-voices__pause');
    var vPauseIcon = voicesSection.querySelector('.sk2-voices__pause-icon');
    var vPlayIcon = voicesSection.querySelector('.sk2-voices__play-icon');
    var vCurrent = 0;
    var vTotal = vGroups.length;
    var vTimer = null;
    var vPaused = false;
    var vVisible = false;
    var V_DURATION = 8000;

    voicesSection.style.setProperty('--sk2-voices-duration', V_DURATION + 'ms');

    function vGoTo(idx) {
      if (idx >= vTotal) idx = 0;
      if (idx < 0) idx = vTotal - 1;

      vGroups[vCurrent].classList.remove('is-active');
      vTabs[vCurrent].classList.remove('is-active');
      vTabs[vCurrent].classList.remove('is-timing');

      vCurrent = idx;

      vGroups[vCurrent].classList.add('is-active');
      vTabs[vCurrent].classList.add('is-active');

      if (!vPaused && vVisible) {
        void vTabs[vCurrent].offsetWidth;
        vTabs[vCurrent].classList.add('is-timing');
      }
    }

    function vSchedule() {
      vStopTimer();
      if (vPaused || !vVisible) return;

      vTabs[vCurrent].classList.add('is-timing');
      vTimer = setTimeout(function () {
        vGoTo(vCurrent + 1);
        vSchedule();
      }, V_DURATION);
    }

    function vStopTimer() {
      clearTimeout(vTimer);
      vTimer = null;
    }

    /* Tab clicks */
    vTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var idx = parseInt(tab.getAttribute('data-sk2-vtab'), 10);
        vGoTo(idx);
        vSchedule();
      });
    });

    /* Pause / play */
    if (vPauseBtn) {
      vPauseBtn.addEventListener('click', function () {
        vPaused = !vPaused;
        if (vPaused) {
          vStopTimer();
          vTabs[vCurrent].classList.remove('is-timing');
          if (vPauseIcon) vPauseIcon.style.display = 'none';
          if (vPlayIcon) vPlayIcon.style.display = 'block';
        } else {
          if (vPauseIcon) vPauseIcon.style.display = 'block';
          if (vPlayIcon) vPlayIcon.style.display = 'none';
          vSchedule();
        }
      });
    }

    /* Visibility gating */
    new IntersectionObserver(function (entries) {
      vVisible = entries[0].isIntersecting;
      if (vVisible && !vPaused) {
        vSchedule();
      } else {
        vStopTimer();
        vTabs[vCurrent].classList.remove('is-timing');
      }
    }, { threshold: 0.1 }).observe(voicesSection);

    /* Tab hidden */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        vStopTimer();
        vTabs[vCurrent].classList.remove('is-timing');
      } else if (vVisible && !vPaused) {
        vSchedule();
      }
    });

    /* Scroll parallax for floating cards (matchMedia-gated) */
    var vStage = voicesSection.querySelector('.sk2-voices__stage');
    if (vStage) {
      var vScrollTicking = false;
      var vParallaxEnabled = false;
      var vParallaxHandler = function () {
        if (!vScrollTicking && vVisible && vParallaxEnabled) {
          requestAnimationFrame(function () {
            var rect = vStage.getBoundingClientRect();
            var center = rect.top + rect.height / 2;
            var viewCenter = window.innerHeight / 2;
            var offset = (center - viewCenter) / window.innerHeight;
            vStage.style.setProperty('--sk2-voices-scroll', offset.toFixed(3));
            vScrollTicking = false;
          });
          vScrollTicking = true;
        }
      };
      var vParallaxMQ = window.matchMedia('(min-width: 768px)');
      function handleVParallaxMQ(mq) {
        vParallaxEnabled = mq.matches;
        if (vParallaxEnabled) {
          window.addEventListener('scroll', vParallaxHandler);
        } else {
          window.removeEventListener('scroll', vParallaxHandler);
          vStage.style.removeProperty('--sk2-voices-scroll');
        }
      }
      vParallaxMQ.addEventListener('change', handleVParallaxMQ);
      handleVParallaxMQ(vParallaxMQ);
    }
  }

  /* -----------------------------------------------------------------------
     CONFIGURATOR — Deadband Selector + Sprite Animation
     ----------------------------------------------------------------------- */
  var configurator = document.querySelector('[data-sk2-configurator]');
  if (configurator) {
    var cfgTabs = configurator.querySelectorAll('[data-cfg-tab]');
    var cfgDetails = configurator.querySelectorAll('[data-cfg-detail]');
    var cfgBgs = configurator.querySelectorAll('[data-cfg-bg]');
    var cfgPhotos = configurator.querySelectorAll('[data-cfg-photo]');
    var cfgThumbs = configurator.querySelectorAll('[data-cfg-thumb]');
    var cfgSprite = configurator.querySelector('[data-cfg-sprite]');
    var cfgPoster = configurator.querySelector('.sk2-configurator__poster');
    var cfgDegreeNum = configurator.querySelector('[data-cfg-degree-num]');
    var cfgFrameCount = cfgSprite ? parseInt(cfgSprite.getAttribute('data-frames'), 10) || 60 : 60;
    var cfgFirstBg = configurator.querySelector('[data-cfg-bg="0"]');
    var cfgFirstPhoto = configurator.querySelector('[data-cfg-photo="0"]');
    var cfgFirstThumb = configurator.querySelector('[data-cfg-thumb="0"]');
    var cfgFirstTab = cfgTabs[0] || null;
    var cfgFirstDetail = configurator.querySelector('[data-cfg-detail="0"]');

    // First detail holds layout space until activation
    if (cfgFirstDetail) cfgFirstDetail.classList.add('is-placeholder');
    var cfgCurrentFrame = 0;
    var cfgAnimating = false;
    var cfgSpriteReady = false;

    // Build a map of frame → degree value for interpolation
    var cfgFrameDegrees = [];
    cfgTabs.forEach(function (tab) {
      cfgFrameDegrees.push({
        frame: parseInt(tab.getAttribute('data-cfg-frame'), 10),
        degrees: parseInt(tab.getAttribute('data-cfg-degrees'), 10)
      });
    });
    cfgFrameDegrees.sort(function (a, b) { return a.frame - b.frame; });

    function cfgInterpolateDegree(frame) {
      if (cfgFrameDegrees.length === 0) return 0;
      if (frame <= cfgFrameDegrees[0].frame) return cfgFrameDegrees[0].degrees;
      if (frame >= cfgFrameDegrees[cfgFrameDegrees.length - 1].frame)
        return cfgFrameDegrees[cfgFrameDegrees.length - 1].degrees;
      for (var i = 0; i < cfgFrameDegrees.length - 1; i++) {
        var a = cfgFrameDegrees[i], b = cfgFrameDegrees[i + 1];
        if (frame >= a.frame && frame <= b.frame) {
          var t = (frame - a.frame) / (b.frame - a.frame);
          return Math.round(a.degrees + (b.degrees - a.degrees) * t);
        }
      }
      return cfgFrameDegrees[0].degrees;
    }

    if (cfgSprite) {
      cfgSprite.addEventListener('load', function () {
        cfgSpriteReady = true;
        if (cfgPoster) cfgPoster.classList.add('is-hidden');
      });
      if (cfgSprite.complete && cfgSprite.naturalHeight > 0) {
        cfgSpriteReady = true;
        if (cfgPoster) cfgPoster.classList.add('is-hidden');
      }
    }

    function cfgShowFrame(frame) {
      if (!cfgSprite || !cfgSpriteReady) return;
      var pct = -(frame / cfgFrameCount) * 100;
      cfgSprite.style.transform = 'translateY(' + pct + '%)';
      // Update degree counter
      if (cfgDegreeNum) cfgDegreeNum.textContent = cfgInterpolateDegree(frame) + '°';
    }

    function cfgEase(t) {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    var cfgAnimId = 0;

    function cfgTweenTo(targetFrame) {
      // Cancel any in-progress animation
      cfgAnimId++;
      var myId = cfgAnimId;
      var startFrame = cfgCurrentFrame;
      var diff = targetFrame - startFrame;
      if (diff === 0) return;
      var duration = Math.abs(diff) * 22;
      duration = Math.max(250, Math.min(duration, 1100));
      var startTime = null;

      function step(time) {
        if (myId !== cfgAnimId) return; // cancelled by a newer click
        if (!startTime) startTime = time;
        var elapsed = time - startTime;
        var t = Math.min(1, elapsed / duration);
        var frame = Math.round(startFrame + diff * t);
        cfgShowFrame(frame);
        cfgCurrentFrame = frame;
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          cfgCurrentFrame = targetFrame;
          cfgShowFrame(targetFrame);
        }
      }
      requestAnimationFrame(step);
    }

    /* -- Entrance animation (scroll-driven) -- */
    var cfgRevealed = false;
    var cfgActivated = false;

    // Animate degree counter from startDeg to endDeg (independent of sprite frames)
    function cfgCountDegrees(endDeg) {
      var startDeg = 0;
      var duration = 450;
      var startTime = null;
      function step(time) {
        if (!startTime) startTime = time;
        var t = Math.min(1, (time - startTime) / duration);
        var deg = Math.round(startDeg + (endDeg - startDeg) * t);
        if (cfgDegreeNum) cfgDegreeNum.textContent = deg + '°';
        if (t < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    function updateCfgEntrance() {
      var rect = configurator.getBoundingClientRect();
      var vh = window.innerHeight;
      // Skip if section is completely off-screen (below viewport or above it)
      if (rect.bottom < 0 || rect.top > vh) return;
      // progress 0→1 as top of section moves from bottom of viewport to 30% from top
      var raw = (vh - rect.top) / (vh * 0.7);
      var progress = Math.max(0, Math.min(1, raw));

      // Background: scroll-driven brightness/saturate/blur
      var bgBrightness = 0.15 + 0.85 * progress;
      var bgSaturate = progress;
      var bgBlur = 6 * (1 - progress);
      configurator.style.setProperty('--cfg-bg-brightness', bgBrightness);
      configurator.style.setProperty('--cfg-bg-saturate', bgSaturate);
      configurator.style.setProperty('--cfg-bg-blur', bgBlur + 'px');

      // Left photo: scroll-driven opacity, slower ramp (0.3→0.8)
      var photoP = Math.max(0, Math.min(1, (progress - 0.3) / 0.5));
      configurator.style.setProperty('--cfg-photo-opacity', photoP);
      if (photoP > 0) {
        if (cfgFirstBg && !cfgFirstBg.classList.contains('is-active')) cfgFirstBg.classList.add('is-active');
        if (cfgFirstPhoto && !cfgFirstPhoto.classList.contains('is-active')) cfgFirstPhoto.classList.add('is-active');
        if (cfgFirstThumb && !cfgFirstThumb.classList.contains('is-active')) cfgFirstThumb.classList.add('is-active');
      }

      // Sprite container: delay start until progress > 0.35, then remap 0.35→1 to 0→1
      var spriteP = Math.max(0, Math.min(1, (progress - 0.5) / 0.5));
      var spriteScale = 0.5 + 0.5 * spriteP;
      var spriteBrightness = 0.35 + 0.65 * spriteP;
      var spriteContrast = 1.4 - 0.4 * spriteP;
      configurator.style.setProperty('--cfg-sprite-scale', spriteScale);
      configurator.style.setProperty('--cfg-sprite-brightness', spriteBrightness);
      configurator.style.setProperty('--cfg-sprite-contrast', spriteContrast);

      // Header text + buttons: reveal at progress > 0.3
      if (progress > 0.3 && !cfgRevealed) {
        cfgRevealed = true;
        configurator.classList.add('cfg-revealed');
      }

      // Button activation: well after everything else is settled
      if (progress >= 1 && !cfgActivated) {
        cfgActivated = true;
        // Remove scroll-driven photo opacity so is-active class takes over
        configurator.style.removeProperty('--cfg-photo-opacity');

        // Colorize buttons just before pressing
        setTimeout(function () {
          configurator.classList.add('cfg-colorized');
        }, 400);

        setTimeout(function () {
          if (cfgFirstTab) cfgFirstTab.classList.add('is-active');

          var firstFrame = cfgFirstTab ? parseInt(cfgFirstTab.getAttribute('data-cfg-frame'), 10) || 0 : 0;
          if (firstFrame > 0) cfgTweenTo(firstFrame);

          // Count degrees 0→9 after a beat so 0° is visible first
          setTimeout(function () {
            var firstDeg = cfgFirstTab ? parseInt(cfgFirstTab.getAttribute('data-cfg-degrees'), 10) || 9 : 9;
            cfgCountDegrees(firstDeg);

            if (cfgFirstDetail) {
              cfgFirstDetail.classList.remove('is-placeholder');
              cfgFirstDetail.classList.add('is-active');
            }
            requestAnimationFrame(function () {
              configurator.classList.add('cfg-activated');
            });
          }, 400);
        }, 600);
      }
    }

    // Crossfade helper: old element exits to black, new fades in on top
    function cfgCrossfade(collection, attr, idx) {
      collection.forEach(function (el) {
        el.classList.remove('is-exiting');
        if (el.classList.contains('is-active')) {
          el.classList.remove('is-active');
          el.classList.add('is-exiting');
        }
      });
      var next = configurator.querySelector('[' + attr + '="' + idx + '"]');
      if (next) { next.classList.remove('is-exiting'); next.classList.add('is-active'); }
    }

    cfgTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-cfg-tab'), 10);
        var targetFrame = parseInt(this.getAttribute('data-cfg-frame'), 10) || 0;

        // Switch active button
        cfgTabs.forEach(function (t) { t.classList.remove('is-active'); });
        this.classList.add('is-active');

        // Crossfade detail panel
        cfgDetails.forEach(function (d) { d.classList.remove('is-active', 'is-placeholder'); });
        var detail = configurator.querySelector('[data-cfg-detail="' + idx + '"]');
        if (detail) detail.classList.add('is-active');

        cfgCrossfade(cfgBgs, 'data-cfg-bg', idx);
        cfgCrossfade(cfgPhotos, 'data-cfg-photo', idx);
        cfgCrossfade(cfgThumbs, 'data-cfg-thumb', idx);

        // Tween sprite to target frame
        cfgTweenTo(targetFrame);
      });
    });

    // Scroll-driven entrance — removed after activation completes
    var cfgTicking = false;
    function onCfgScroll() {
      if (!cfgTicking) {
        requestAnimationFrame(function () {
          updateCfgEntrance();
          cfgTicking = false;
          if (cfgRevealed && cfgActivated) window.removeEventListener('scroll', onCfgScroll);
        });
        cfgTicking = true;
      }
    }
    window.addEventListener('scroll', onCfgScroll);
    // Run immediately and again after a frame to catch mid-page reloads
    updateCfgEntrance();
    requestAnimationFrame(function () { updateCfgEntrance(); });
  }

  /* -----------------------------------------------------------------------
     CINEMATIC PAUSE — Word-by-word scroll reveal
     ----------------------------------------------------------------------- */
  var pauseEl = document.querySelector('[data-sk2-pause-words]');
  if (pauseEl) {
    var pauseSection = pauseEl.closest('.sk2-pause');
    var pauseWordCount = pauseEl.querySelectorAll('.sk2-pause__word').length;
    var pauseTicking = false;

    function updatePauseWords() {
      var rect = pauseSection.getBoundingClientRect();
      var vh = window.innerHeight;
      if (rect.bottom < 0 || rect.top > vh) return;
      var raw = (vh * 0.5 - rect.top) / (vh * 0.4);
      var progress = raw < 0 ? 0 : raw > 1 ? 1 : raw;
      pauseSection.style.setProperty('--pause-words', progress * pauseWordCount);
    }

    window.addEventListener('scroll', function () {
      if (!pauseTicking) {
        requestAnimationFrame(function () {
          updatePauseWords();
          pauseTicking = false;
        });
        pauseTicking = true;
      }
    });
    updatePauseWords();
    requestAnimationFrame(function () { updatePauseWords(); });
  }

  /* -----------------------------------------------------------------------
     BEFORE/AFTER — Drag Slider (rAF-throttled)
     ----------------------------------------------------------------------- */
  var slider = document.querySelector('.sk2-beforeafter__slider');
  if (slider) {
    var handle = slider.querySelector('.sk2-beforeafter__handle');
    var leftSide = slider.querySelector('.sk2-beforeafter__side--left');
    var isDragging = false;
    var sliderTicking = false;
    var lastPointerX = 0;

    function updateSlider() {
      var rect = slider.getBoundingClientRect();
      var pos = Math.max(0, Math.min(1, (lastPointerX - rect.left) / rect.width));
      var pct = pos * 100;
      if (leftSide) leftSide.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
      if (handle) handle.style.left = pct + '%';
      sliderTicking = false;
    }

    slider.addEventListener('pointerdown', function (e) {
      isDragging = true;
      slider.setPointerCapture(e.pointerId);
      lastPointerX = e.clientX;
      requestAnimationFrame(updateSlider);
    });

    slider.addEventListener('pointermove', function (e) {
      if (!isDragging) return;
      lastPointerX = e.clientX;
      if (!sliderTicking) {
        requestAnimationFrame(updateSlider);
        sliderTicking = true;
      }
    });

    slider.addEventListener('pointerup', function () {
      isDragging = false;
    });
  }

  /* -----------------------------------------------------------------------
     VERSUS — Tabbed Comparison
     ----------------------------------------------------------------------- */
  var versusTabs = document.querySelectorAll('[data-sk2-versus-tab]');
  var versusPanels = document.querySelectorAll('[data-sk2-versus-panel]');
  if (versusTabs.length) {
    versusTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var idx = tab.getAttribute('data-sk2-versus-tab');
        versusTabs.forEach(function (t) { t.classList.remove('is-active'); });
        versusPanels.forEach(function (p) { p.classList.remove('is-active'); });
        tab.classList.add('is-active');
        var panel = document.querySelector('[data-sk2-versus-panel="' + idx + '"]');
        if (panel) panel.classList.add('is-active');
      });
    });
  }

  /* -----------------------------------------------------------------------
     SHOP — Carousel + Add to Cart
     ----------------------------------------------------------------------- */
  var shopSection = document.querySelector('[data-sk2-shop]');
  if (shopSection) {
    function sk2AddToCart(items, btn) {
      var originalText = btn.textContent;
      btn.textContent = 'Adding...';
      btn.disabled = true;

      // Get the cart drawer element for section rendering
      var cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var sectionsToRender = cartDrawer && cartDrawer.getSectionsToRender
        ? cartDrawer.getSectionsToRender().map(function (s) { return s.id; })
        : [];

      var body = { items: items };
      if (sectionsToRender.length) {
        body.sections = sectionsToRender.join(',');
        body.sections_url = window.location.pathname;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      .then(function (res) { return res.json(); })
      .then(function (response) {
        // Shopify returns { status: 422, description: '...' } for errors
        if (response.status && response.status >= 400) {
          console.warn('[SK2 Shop] Shopify error:', response.status, response.description);
          btn.textContent = originalText;
          btn.disabled = false;
          var actionsEl = btn.closest('.sk2-shop__details') || btn.parentElement;
          var errMsg = actionsEl.querySelector('.sk2-shop__error');
          if (!errMsg) {
            errMsg = document.createElement('p');
            errMsg.className = 'sk2-shop__error';
            actionsEl.appendChild(errMsg);
          }
          errMsg.textContent = 'Max quantity available already in cart.';
          setTimeout(function () { if (errMsg.parentElement) errMsg.remove(); }, 5000);
          return;
        }

        btn.textContent = 'Added \u2713';
        btn.disabled = false;

        // Update cart drawer via Dawn's renderContents (opens drawer + updates count)
        try {
          if (cartDrawer && cartDrawer.renderContents && response.sections) {
            cartDrawer.renderContents(response);
          }
        } catch (e) {
          console.warn('[SK2 Shop] renderContents error:', e);
        }

        // Publish cart update event for other Dawn components
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sk2-shop', productVariantId: items[0].id });
          }
        } catch (e) {
          console.warn('[SK2 Shop] publish error:', e);
        }

        setTimeout(function () { btn.textContent = originalText; }, 2000);
      })
      .catch(function (err) {
        console.error('[SK2 Shop] Add to cart failed:', err);
        btn.textContent = 'Error';
        btn.disabled = false;
        setTimeout(function () { btn.textContent = originalText; }, 3000);
      });
    }

    // Variant selection logic
    shopSection.querySelectorAll('[data-shop-product]').forEach(function (productEl) {
      var jsonEl = productEl.querySelector('[data-shop-variant-json]');
      if (!jsonEl) return;
      var variants = [];
      try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }
      var selects = productEl.querySelectorAll('[data-shop-option]');
      if (!selects.length) return;

      function findVariant() {
        var selected = [];
        selects.forEach(function (sel) {
          selected.push(sel.value);
        });
        for (var i = 0; i < variants.length; i++) {
          var match = true;
          for (var j = 0; j < selected.length; j++) {
            if (variants[i].options[j] !== selected[j]) { match = false; break; }
          }
          if (match) return variants[i];
        }
        return null;
      }

      function updateVariant() {
        var v = findVariant();
        var priceEl = productEl.querySelector('[data-shop-price]');
        var atcWrap = productEl.querySelector('[data-shop-atc-wrap]');
        if (!v || !atcWrap) return;

        if (priceEl) priceEl.textContent = v.price;

        // Replace only the cart button, preserve View Details link
        var oldBtn = atcWrap.querySelector('.sk2-shop__atc');
        if (v.available) {
          var newBtn = document.createElement('button');
          newBtn.type = 'button';
          newBtn.className = 'sk2-btn sk2-btn--primary sk2-shop__atc';
          newBtn.setAttribute('data-sk2-add-to-cart', v.id);
          newBtn.textContent = 'Add to Cart';
          newBtn.addEventListener('click', function () {
            sk2AddToCart([{ id: v.id, quantity: 1 }], newBtn);
          });
          if (oldBtn) oldBtn.replaceWith(newBtn); else atcWrap.prepend(newBtn);
        } else {
          var soldBtn = document.createElement('button');
          soldBtn.type = 'button';
          soldBtn.className = 'sk2-btn sk2-btn--primary sk2-shop__atc sk2-shop__sold-out';
          soldBtn.disabled = true;
          soldBtn.textContent = 'Sold Out';
          if (oldBtn) oldBtn.replaceWith(soldBtn); else atcWrap.prepend(soldBtn);
        }
      }

      selects.forEach(function (sel) {
        sel.addEventListener('change', updateVariant);
      });
    });

    // Add to cart — initial buttons
    shopSection.querySelectorAll('[data-sk2-add-to-cart]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = parseInt(this.getAttribute('data-sk2-add-to-cart'), 10);
        sk2AddToCart([{ id: id, quantity: 1 }], this);
      });
    });

    // Rear / Front toggle per tier card
    shopSection.querySelectorAll('[data-shop-tier]').forEach(function (tier) {
      tier.querySelectorAll('[data-shop-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = this.getAttribute('data-shop-toggle');
          tier.querySelectorAll('[data-shop-toggle]').forEach(function (b) { b.classList.remove('is-active'); });
          this.classList.add('is-active');
          tier.querySelectorAll('[data-shop-view]').forEach(function (v) { v.classList.remove('is-active'); });
          var view = tier.querySelector('[data-shop-view="' + key + '"]');
          if (view) view.classList.add('is-active');
          tier.querySelectorAll('[data-shop-info]').forEach(function (i) { i.classList.remove('is-active'); });
          var info = tier.querySelector('[data-shop-info="' + key + '"]');
          if (info) info.classList.add('is-active');
        });
      });
    });

    // Carousel — native scroll-snap
    var shopTrack = shopSection.querySelector('[data-shop-track]');
    var shopSlides = Array.prototype.slice.call(shopSection.querySelectorAll('[data-shop-slide]'));
    var shopTabs = shopSection.querySelectorAll('[data-shop-tab]');
    var shopPrev = shopSection.querySelector('[data-shop-prev]');
    var shopNext = shopSection.querySelector('[data-shop-next]');
    var shopPill = shopSection.querySelector('.sk2-shop__pill');
    var shopCurrent = 0;
    var shopTotal = shopSlides.length;

    function shopScrollPillToActive(tabEl) {
      if (!shopPill || !tabEl) return;
      var pillRect = shopPill.getBoundingClientRect();
      var tabRect = tabEl.getBoundingClientRect();
      var offset = tabRect.left - pillRect.left + shopPill.scrollLeft - (pillRect.width - tabRect.width) / 2;
      shopPill.scrollTo({ left: offset, behavior: 'smooth' });
    }

    function shopSyncActive() {
      var trackRect = shopTrack.getBoundingClientRect();
      var center = trackRect.left + trackRect.width / 2;
      var closest = 0;
      var closestDist = Infinity;

      // Read-only: find which slide is closest to center
      shopSlides.forEach(function (slide, i) {
        var rect = slide.getBoundingClientRect();
        var dist = Math.abs(rect.left + rect.width / 2 - center);
        if (dist < closestDist) { closestDist = dist; closest = i; }
      });

      // Write only when active index changes (minimizes DOM writes)
      if (closest !== shopCurrent) {
        shopCurrent = closest;
        shopTabs.forEach(function (t) { t.classList.remove('is-active'); });
        if (shopTabs[shopCurrent]) {
          shopTabs[shopCurrent].classList.add('is-active');
          shopScrollPillToActive(shopTabs[shopCurrent]);
        }
        shopSlides.forEach(function (s) { s.classList.remove('is-active'); });
        shopSlides[shopCurrent].classList.add('is-active');
        shopUpdateArrows();
      }
    }

    function shopScrollTo(idx) {
      if (idx < 0) idx = 0;
      if (idx >= shopTotal) idx = shopTotal - 1;
      var slide = shopSlides[idx];
      if (!slide) return;
      var trackLeft = shopTrack.getBoundingClientRect().left;
      var slideLeft = slide.getBoundingClientRect().left;
      var offset = slideLeft - trackLeft + shopTrack.scrollLeft - (shopTrack.offsetWidth - slide.offsetWidth) / 2;
      shopTrack.scrollTo({ left: offset, behavior: 'smooth' });
    }

    function shopUpdateArrows() {
      if (shopPrev) shopPrev.disabled = (shopCurrent === 0);
      if (shopNext) shopNext.disabled = (shopCurrent === shopTotal - 1);
    }

    // Init first slide
    if (shopSlides[0]) shopSlides[0].classList.add('is-active');
    shopSyncActive();
    shopUpdateArrows();

    var shopScrollTicking = false;
    shopTrack.addEventListener('scroll', function () {
      if (!shopScrollTicking) {
        requestAnimationFrame(function () {
          shopSyncActive();
          shopScrollTicking = false;
        });
        shopScrollTicking = true;
      }
    }, { passive: true });

    shopTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        shopScrollTo(parseInt(this.getAttribute('data-shop-tab'), 10));
      });
    });

    if (shopPrev) shopPrev.addEventListener('click', function () { shopScrollTo(shopCurrent - 1); });
    if (shopNext) shopNext.addEventListener('click', function () { shopScrollTo(shopCurrent + 1); });

    // Click inactive slide to navigate to it (block links/buttons on inactive cards)
    shopSlides.forEach(function (slide, i) {
      slide.addEventListener('click', function (e) {
        if (i !== shopCurrent) {
          e.preventDefault();
          e.stopPropagation();
          shopScrollTo(i);
        }
      }, true);
    });
  }

  /* -----------------------------------------------------------------------
     SPECS — Rear / Front tab switcher
     ----------------------------------------------------------------------- */
  document.querySelectorAll('[data-specs-tab]').forEach(function (tab) {
    tab.addEventListener('click', function () {
      var key = this.getAttribute('data-specs-tab');
      document.querySelectorAll('[data-specs-tab]').forEach(function (t) { t.classList.remove('is-active'); });
      document.querySelectorAll('[data-specs-panel]').forEach(function (p) { p.classList.remove('is-active'); });
      this.classList.add('is-active');
      var panel = document.querySelector('[data-specs-panel="' + key + '"]');
      if (panel) panel.classList.add('is-active');
    });
  });

  /* -----------------------------------------------------------------------
     FAQ — Accordion
     ----------------------------------------------------------------------- */
  document.querySelectorAll('.sk2-faq__question').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var item = btn.closest('.sk2-faq__item');
      if (!item) return;
      var answer = item.querySelector('.sk2-faq__answer');
      if (!answer) return;
      var isOpen = item.classList.contains('is-open');

      document.querySelectorAll('.sk2-faq__item').forEach(function (i) {
        i.classList.remove('is-open');
        var a = i.querySelector('.sk2-faq__answer');
        if (a) a.style.maxHeight = '0';
      });

      if (!isOpen) {
        item.classList.add('is-open');
        answer.style.maxHeight = answer.scrollHeight + 'px';
      }
    });
  });

  /* -----------------------------------------------------------------------
     EXPLODED — Hub feature tour (scroll-driven pan/zoom)
     ----------------------------------------------------------------------- */
  var exploded = document.querySelector('[data-sk2-exploded]');
  if (exploded) {
    var exTicking = false;
    var exVisible = false;

    var exFocusData = [];
    var exFocusEl = exploded.querySelector('[data-sk2-exploded-focus]');
    if (exFocusEl) {
      try { exFocusData = JSON.parse(exFocusEl.textContent); } catch (e) {}
    }
    var exCount = exFocusData.length || 1;
    var exDefault = { x: 50, y: 50 };
    var exImg = exploded.querySelector('.sk2-exploded__hub-img:not(.sk2-exploded__hub-img--reveal)');
    var exHlDot = exploded.querySelector('.sk2-exploded__hl-dot');
    var exHlBack = exploded.querySelector('.sk2-exploded__hl-back');
    var exSticky = exploded.querySelector('.sk2-exploded__sticky');
    var exFeatures = exploded.querySelectorAll('.sk2-exploded__feature');
    var exLastActiveIdx = -1;
    var exLastDeadzone = false;
    var exMobile = window.matchMedia('(max-width: 767px)').matches;

    function exClamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

    // Debug tool: ?debug=exploded — click the hub to log focus coordinates
    if (window.location.search.indexOf('debug=exploded') > -1) {
      var exWrap = exploded.querySelector('.sk2-exploded__hub-wrap');
      var exHub = exploded.querySelector('.sk2-exploded__hub');
      if (exWrap && exHub) {
        exHub.style.pointerEvents = 'auto';
        exWrap.style.pointerEvents = 'auto';
        exWrap.style.cursor = 'crosshair';
        exWrap.style.zIndex = '999';
        var exDebugCount = 0;

        exWrap.addEventListener('click', function (e) {
          var rect = exWrap.getBoundingClientRect();
          var x = Math.round((e.clientX - rect.left) / rect.width * 100);
          var y = Math.round((e.clientY - rect.top) / rect.height * 100);
          exDebugCount++;

          // Place a visible dot where you clicked
          var dot = document.createElement('div');
          dot.style.cssText = 'position:absolute;width:16px;height:16px;border-radius:50%;background:#00ff00;border:2px solid #fff;transform:translate(-50%,-50%);z-index:999;pointer-events:none;box-shadow:0 0 12px rgba(0,255,0,0.6),0 0 4px rgba(0,0,0,0.8);';
          dot.style.left = x + '%';
          dot.style.top = y + '%';
          exWrap.appendChild(dot);

          // Label
          var label = document.createElement('div');
          label.style.cssText = 'position:absolute;transform:translate(-50%,12px);z-index:999;pointer-events:none;font:bold 13px monospace;color:#00ff00;text-shadow:0 1px 4px #000,0 0 8px #000;white-space:nowrap;';
          label.style.left = x + '%';
          label.style.top = y + '%';
          label.textContent = '#' + exDebugCount + ': ' + x + ', ' + y;
          exWrap.appendChild(label);

          // Log to console
          console.log(
            '%c[Exploded Debug] Point #' + exDebugCount +
            ' %cfocus_x: ' + x + ', focus_y: ' + y,
            'color:#97d5ca;font-weight:bold',
            'color:#fff'
          );
          console.log('  Shopify settings → focus_x: ' + x + ', focus_y: ' + y + ', focus_zoom: 14');
        });

        console.log(
          '%c[Exploded Debug] Active — click on the hub image to log coordinates.',
          'color:#97d5ca;font-weight:bold;font-size:14px'
        );
        console.log('  Each click places a dot and logs focus_x / focus_y values.');
        console.log('  Copy the values into the Shopify section settings.');
      }
    }

    function updateExploded() {
      exTicking = false;
      if (!exVisible) return;

      var ih = window.innerHeight;
      var rect = exploded.getBoundingClientRect();
      var scrollRange = exploded.offsetHeight - ih;
      if (scrollRange <= 0) return;
      var progress = exClamp01(-rect.top / scrollRange);

      // Features and reveal happen together — reveal completes during the
      // first half of the first feature's scroll range.
      var feature = progress * exCount;
      exploded.style.setProperty('--feature', feature);

      var reveal = exClamp01(feature / 0.4);
      exploded.style.setProperty('--reveal', reveal);

      // Hub stays fixed — only scale up during reveal, then hold
      var revealScale = 1.0 + reveal * 0.2;
      exploded.style.setProperty('--hub-scale', revealScale);

      // Active feature and dead zone
      var activeIdx = Math.max(0, Math.min(Math.floor(feature), exCount - 1));
      var t = feature - activeIdx;
      var inDeadzone = feature >= 0.25 && t >= 0.25 && t < 0.95;

      // Only update position/classes when active feature changes
      if (activeIdx !== exLastActiveIdx) {
        var pt = exFocusData[activeIdx] || exDefault;
        exploded.style.setProperty('--hl-x', pt.x);
        exploded.style.setProperty('--hl-y', pt.y);

        // Mobile: pan hub so the focus point is visible in viewport.
        // Use viewport width * 1.6 (= 160vw) directly — don't rely on
        // offsetWidth which can be stale or incorrect during transitions.
        if (exMobile) {
          var vw = window.innerWidth;
          var hubW = vw * 1.6;
          var txIdeal = hubW * (0.5 - pt.x / 100);
          var maxTx = (hubW - vw) / 2;
          var txMobile = Math.max(-maxTx, Math.min(maxTx, txIdeal));
          exploded.style.setProperty('--hub-tx-mobile', txMobile + 'px');
        }

        exFeatures.forEach(function (f, i) {
          f.classList.toggle('is-past', i < activeIdx);
        });
        exLastActiveIdx = activeIdx;
      }


      // Only toggle dead zone classes when state changes
      if (inDeadzone !== exLastDeadzone) {
        if (exHlDot) exHlDot.classList.toggle('is-visible', inDeadzone);
        if (exHlBack) exHlBack.classList.toggle('is-active', inDeadzone);
        exLastDeadzone = inDeadzone;
      }


    }

    new IntersectionObserver(
      function (entries) {
        exVisible = entries[0].isIntersecting;
        if (exVisible) updateExploded();
      },
      { rootMargin: '200px 0px' }
    ).observe(exploded);

    window.addEventListener('scroll', function () {
      if (!exTicking) {
        requestAnimationFrame(updateExploded);
        exTicking = true;
      }
    });

    var exInitRect = exploded.getBoundingClientRect();
    exVisible = exInitRect.bottom > -200 && exInitRect.top < window.innerHeight + 200;
    updateExploded();

    var exResizeRaf = false;
    window.addEventListener('resize', function () {
      if (exResizeRaf) return;
      exResizeRaf = true;
      requestAnimationFrame(function () {
        exResizeRaf = false;
        exMobile = window.matchMedia('(max-width: 767px)').matches;
        if (!exMobile) exploded.style.removeProperty('--hub-tx-mobile');
        exLastActiveIdx = -1;
        var r = exploded.getBoundingClientRect();
        exVisible = r.bottom > -200 && r.top < window.innerHeight + 200;
        updateExploded();
      });
    });

  }

})();
